import {
  fetchStacks,
  getStackType,
  selectStacksByOrgAsList,
} from "@app/deploy";
import { selectOrganizationSelected } from "@app/organizations";
import { useLoader, useQuery, useSelector } from "@app/react";
import { createStackUrl } from "@app/routes";
import type { WebState } from "@app/schema";
import { useSecurityControlsNav } from "@app/ui/hooks";
import {
  Banner,
  ButtonLink,
  IconCheck,
  IconDownload,
  IconExclamationCircle,
  IconExternalLink,
  IconLock,
  IconX,
  Modal,
  ModalBody,
  ModalHeading,
  ModalOverlay,
  Tooltip,
} from "@app/ui/shared";
import {
  CONTROLS_EVENTS,
  RadialProgress,
  controlsEvent,
} from "@app/ui/shared/security-controls";
import { tokens } from "@app/ui/shared/tokens";
import cn from "classnames";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  type ComplianceModalCtaAction,
  HOME_DASH_EVENTS,
  homeDashEvent,
  homeDashOrgPayload,
} from "./tuna-events";

const LOGO_BASE_PATH = "/assets/images/frameworks";
const BADGE_BASE_PATH = "/assets/images/badges";

type ComplianceCardId =
  | "infrastructure"
  | "hipaa-required"
  | "hipaa-addressable"
  | "hitrust"
  | "pci"
  | "pipeda";

interface ComplianceCardProps {
  id: ComplianceCardId;
  title: string;
  subtitle?: string;
  description: React.ReactNode;
  logo?: string;
  badgeAsset?: string;
  actionLabel?: string;
  actionHref?: string;
  score: ComplianceScore | null;
  onClick: () => void;
}

type ComplianceScore = 0 | 80 | 100;

const DEDICATED_STACK_SCORES: Record<ComplianceCardId, ComplianceScore> = {
  infrastructure: 100,
  "hipaa-required": 100,
  "hipaa-addressable": 80,
  hitrust: 80,
  pci: 100,
  pipeda: 100,
};

const SHARED_STACK_SCORES: Record<ComplianceCardId, ComplianceScore> = {
  infrastructure: 80,
  "hipaa-required": 0,
  "hipaa-addressable": 0,
  hitrust: 0,
  pci: 0,
  pipeda: 0,
};

const getComplianceScore = (
  id: ComplianceCardId,
  hasDedicatedStack: boolean,
): ComplianceScore =>
  hasDedicatedStack ? DEDICATED_STACK_SCORES[id] : SHARED_STACK_SCORES[id];

const COMPLIANCE_SCORE_TOOLTIP: Record<ComplianceScore, string> = {
  100: "Infrastructure controls automated and enforced by Aptible",
  80: "See compliance dashboard to define scope",
  0: "Requires a dedicated stack",
};

const isDedicatedTenancyStack = (stack: Parameters<typeof getStackType>[0]) => {
  const stackType = getStackType(stack);
  return stackType === "dedicated" || stackType === "self_hosted";
};

const STATUS_ICON_SIZE = "h-12 w-12";
const STATUS_RADIUS = 20;

const renderComplianceScoreIcon = (score: ComplianceScore) => {
  if (score === 100) {
    return (
      <span
        className={cn(
          "relative inline-flex items-center justify-center rounded-full bg-emerald-100",
          STATUS_ICON_SIZE,
        )}
        aria-hidden
      >
        <RadialProgress
          progress={100}
          radius={STATUS_RADIUS}
          strokeWidth={3}
          variant="success"
        />
        <span
          className="absolute inset-0 flex items-center justify-center"
          aria-hidden
        >
          <IconCheck variant="sm" color="#00633F" aria-hidden />
        </span>
      </span>
    );
  }

  if (score === 0) {
    return (
      <span
        className={cn(
          "inline-flex items-center justify-center rounded-full bg-red-100",
          STATUS_ICON_SIZE,
        )}
        aria-hidden
      >
        <IconExclamationCircle color="#EF4444" aria-hidden />
      </span>
    );
  }

  return (
    <span
      className={cn(
        "inline-flex items-center justify-center rounded-full bg-amber-100",
        STATUS_ICON_SIZE,
      )}
      aria-hidden
    >
      <RadialProgress
        progress={score}
        radius={STATUS_RADIUS}
        strokeWidth={3}
        variant="warn"
      />
    </span>
  );
};

const ComplianceCard = ({
  id,
  title,
  subtitle,
  score,
  logo,
  onClick,
}: ComplianceCardProps) => {
  return (
    <div className="flex min-w-0 h-full flex-col bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={onClick}
        className="w-full text-left p-4 flex items-center gap-3 hover:bg-gray-50 transition-colors flex-shrink-0"
      >
        {score !== null && (
          <div className="flex-shrink-0 flex items-center">
            <Tooltip text={COMPLIANCE_SCORE_TOOLTIP[score]} placement="top">
              <span className="inline-flex items-center">
                {renderComplianceScoreIcon(score)}
              </span>
            </Tooltip>
          </div>
        )}
        <div className="flex min-w-0 flex-1 flex-col">
          <h3 className="text-base font-semibold leading-tight text-gray-900">
            {title}
          </h3>
          {subtitle ? (
            <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>
          ) : null}
        </div>
        {/* Framework icon on right; Infrastructure card intentionally has no logo */}
        {logo ? (
          <div className="flex-shrink-0 flex items-center justify-center">
            <img
              src={`${LOGO_BASE_PATH}/${logo}`}
              alt=""
              className="w-10 h-10 object-contain"
            />
          </div>
        ) : null}
      </button>
    </div>
  );
};

const CARD_CONFIG: Array<{
  id: ComplianceCardId;
  title: string;
  subtitle?: string;
  modalSubtitle: string;
  description: React.ReactNode;
  logo?: string;
  badgeAsset?: string;
  actionLabel?: string;
  actionHref?: string;
  docsHref?: string;
  getScore: (opts: {
    hasDedicatedStack: boolean;
    hasDedicatedStackInCaCentral: boolean;
  }) => ComplianceScore;
}> = [
  {
    id: "infrastructure",
    title: "Infrastructure Security",
    modalSubtitle:
      "Infrastructure built with security best practices, enforced by default",
    badgeAsset: "secured-by-aptible.png",
    actionLabel: "View Trust Center",
    actionHref: "https://trust.aptible.com/",
    docsHref:
      "https://www.aptible.com/docs/core-concepts/security-compliance/overview",
    description: (
      <div className="text-sm text-gray-600 space-y-3">
        <p>
          Aptible implements and manages the core infrastructure security
          controls required to support production workloads.
        </p>
        <p>
          This includes hardened network isolation, encryption in transit and at
          rest, intrusion detection, vulnerability management, strict access
          controls, and continuous monitoring.
        </p>
        <p>
          These safeguards are enforced and maintained by Aptible at the
          infrastructure layer and independently audited under frameworks such
          as SOC 2 Type 2, HITRUST, and PCI DSS. Together, they form the secure
          foundation for your applications and data.
        </p>
      </div>
    ),
    getScore: ({ hasDedicatedStack }) =>
      getComplianceScore("infrastructure", hasDedicatedStack),
  },
  {
    id: "hipaa-required",
    title: "HIPAA Required",
    subtitle: "Healthcare",
    modalSubtitle: "HIPAA-required controls 100% enforced by default",
    badgeAsset: "hipaa-compliant.png",
    docsHref:
      "https://www.aptible.com/docs/core-concepts/security-compliance/compliance-frameworks/hipaa",
    description: (
      <div className="text-sm text-gray-600 space-y-3">
        <p>
          When deploying to a Dedicated Stack, Aptible automatically enforces
          the infrastructure controls required to support HIPAA compliance,
          including encryption in transit and at rest, audit logging, access
          controls, and network isolation.
        </p>
        <p>
          These technical safeguards are implemented and maintained by Aptible
          at the infrastructure layer and enforced by default without manual
          configuration.
        </p>
        <p>
          Aptible also provides and executes a Business Associate Agreement
          (BAA) as part of Dedicated Stack setup.
        </p>
        <p>
          Together, this establishes a HIPAA-ready infrastructure baseline that
          enables you to begin processing PHI from day one.
        </p>
      </div>
    ),
    logo: "hipaa.png",
    getScore: ({ hasDedicatedStack }) =>
      getComplianceScore("hipaa-required", hasDedicatedStack),
  },
  {
    id: "hipaa-addressable",
    title: "HIPAA Addressable",
    subtitle: "Healthcare",
    modalSubtitle: "Configurable controls to strengthen HIPAA posture",
    badgeAsset: "hipaa-compliant.png",
    docsHref:
      "https://www.aptible.com/docs/core-concepts/security-compliance/compliance-frameworks/hipaa",
    description: (
      <div className="text-sm text-gray-600 space-y-3">
        <p>
          Aptible enforces 100% of required controls and satisfies many
          addressable safeguards by default within a Dedicated Stack.
        </p>
        <p>
          Additional safeguards are available as configurable options, including
          enabling MFA or SSO, scaling to multiple containers for high
          availability, and configuring cross-region copy backups.
        </p>
        <p>
          These safeguards can be enabled through simple configuration changes,
          with guided workflows in the Security & Compliance Dashboard to review
          resources in scope, track status, and maintain audit-ready evidence.
        </p>
      </div>
    ),
    logo: "hipaa.png",
    getScore: ({ hasDedicatedStack }) =>
      getComplianceScore("hipaa-addressable", hasDedicatedStack),
  },
  {
    id: "hitrust",
    title: "HITRUST",
    subtitle: "Healthcare",
    modalSubtitle: "HITRUST inheritance to fast-track certification",
    badgeAsset: "hitrust-compliant.png",
    docsHref:
      "https://www.aptible.com/docs/core-concepts/security-compliance/compliance-frameworks/hitrust",
    description: (
      <div className="text-sm text-gray-600 space-y-3">
        <p>
          Aptible provides HITRUST Inheritance, allowing customers to inherit a
          significant portion of applicable infrastructure controls through the
          HITRUST MyCSF platform. This reduces the number of controls your team
          must independently implement, test, and document.
        </p>
        <p>
          By leveraging Aptible's audited infrastructure controls, you can
          streamline assessment scope, reduce certification effort, and shorten
          time to validation while maintaining visibility into both inherited
          and customer-responsible controls.
        </p>
      </div>
    ),
    logo: "hitrust.png",
    getScore: ({ hasDedicatedStack }) =>
      getComplianceScore("hitrust", hasDedicatedStack),
  },
  {
    id: "pci",
    title: "PCI DSS",
    subtitle: "Finance",
    modalSubtitle:
      "PCI-ready infrastructure with required technical safeguards enforced",
    badgeAsset: "pci-compliant.png",
    actionLabel: "View Trust Center",
    actionHref: "https://trust.aptible.com/",
    docsHref:
      "https://www.aptible.com/docs/core-concepts/security-compliance/compliance-frameworks/pci",
    description: (
      <div className="text-sm text-gray-600 space-y-3">
        <p>
          When deploying to a Dedicated Stack, Aptible enforces infrastructure
          controls designed to support PCI DSS requirements.
        </p>
        <p>
          These controls include encryption in transit and at rest, network
          isolation, access controls, logging, and other technical safeguards
          implemented and maintained by Aptible at the infrastructure layer.
        </p>
        <p>
          This provides a secure foundation for handling and processing payment
          card data while supporting your broader PCI compliance efforts.
        </p>
      </div>
    ),
    logo: "pci.png",
    getScore: ({ hasDedicatedStack }) =>
      getComplianceScore("pci", hasDedicatedStack),
  },
  {
    id: "pipeda",
    title: "PIPEDA",
    subtitle: "Privacy, Canada",
    modalSubtitle:
      "Secure infrastructure to support PIPEDA privacy requirements",
    badgeAsset: "pipeda-compliant.png",
    logo: "pipeda.png",
    actionLabel: "View Trust Center",
    actionHref: "https://trust.aptible.com/",
    docsHref:
      "https://www.aptible.com/docs/core-concepts/security-compliance/compliance-frameworks/pipeda",
    description: (
      <div className="text-sm text-gray-600 space-y-3">
        <p>
          Aptible provides the secure infrastructure and technical safeguards
          required to protect personal information under the Personal
          Information Protection and Electronic Documents Act (PIPEDA).
        </p>
        <p>
          When deployed to a Dedicated Stack, Aptible automatically enforces
          critical controls such as encryption in transit and at rest, strong
          access controls, audit logging, and network isolation. These
          safeguards establish a secure foundation aligned with PIPEDA&apos;s
          expectations for protecting personal data.
        </p>
      </div>
    ),
    getScore: ({ hasDedicatedStackInCaCentral }) =>
      hasDedicatedStackInCaCentral ? 100 : 0,
  },
];

export const SecurityCompliance = () => {
  const org = useSelector(selectOrganizationSelected);
  useQuery(fetchStacks());
  const stacksLoader = useLoader(fetchStacks);
  const stacks = useSelector((state: WebState) =>
    selectStacksByOrgAsList(state),
  );
  const {
    canAccess: canSeeSecurityDashboard,
    destination: securityControlsDestination,
  } = useSecurityControlsNav();
  const isStacksLoading = stacksLoader.isInitialLoading || !stacks;

  const hasDedicatedStack =
    !isStacksLoading && stacks
      ? stacks.some((stack) => isDedicatedTenancyStack(stack))
      : false;
  const hasDedicatedStackInCaCentral =
    !isStacksLoading && stacks
      ? stacks.some(
          (stack) =>
            isDedicatedTenancyStack(stack) && stack.region === "ca-central-1",
        )
      : false;
  const visibleCardConfig = CARD_CONFIG.filter((config) => {
    if (config.id === "pipeda") return hasDedicatedStackInCaCentral;
    return true;
  });

  const hasNeedsAttention = !isStacksLoading && !hasDedicatedStack;

  const [openCardId, setOpenCardId] = useState<ComplianceCardId | null>(null);
  const [badgePreview, setBadgePreview] = useState<string | null>(null);
  const totalCards = visibleCardConfig.length;
  const gridClass =
    totalCards === 5
      ? "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 2xl:grid-cols-5 gap-x-4 gap-y-2 items-stretch min-w-0"
      : "grid grid-cols-1 gap-x-4 gap-y-2 items-stretch sm:grid-cols-2 md:grid-cols-3";

  const openConfig =
    openCardId !== null
      ? visibleCardConfig.find((c) => c.id === openCardId)
      : null;
  const openCardScore =
    openConfig && !isStacksLoading
      ? openConfig.getScore({
          hasDedicatedStack,
          hasDedicatedStackInCaCentral,
        })
      : null;

  useEffect(() => {
    if (openCardId === "pipeda" && !hasDedicatedStackInCaCentral) {
      setOpenCardId(null);
    }
  }, [openCardId, hasDedicatedStackInCaCentral]);

  const fireModalCta = (action: ComplianceModalCtaAction) => {
    if (!openCardId) return;
    homeDashEvent(HOME_DASH_EVENTS.COMPLIANCE_MODAL_CTA, {
      action,
      cardId: openCardId,
      ...homeDashOrgPayload(org),
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className={cn("mb-4", tokens.type.h3)}>Security & Compliance</h2>
        {hasNeedsAttention && (
          <Banner variant="info" className="mb-4 flex items-center">
            <div className="flex flex-1 flex-wrap items-center justify-between gap-3">
              <span className="text-sm text-gray-700">
                To get started with compliance-ready infrastructure, provision a
                Dedicated Stack.
              </span>
              <ButtonLink
                to={createStackUrl()}
                variant="white"
                size="sm"
                className="flex-shrink-0"
                onClick={() =>
                  homeDashEvent(
                    HOME_DASH_EVENTS.COMPLIANCE_DEDICATED_STACK_CTA,
                    {
                      destination: "create_stack",
                      ...homeDashOrgPayload(org),
                    },
                  )
                }
              >
                Create Stack
              </ButtonLink>
            </div>
          </Banner>
        )}
        <div className="space-y-2">
          <div className={gridClass}>
            {visibleCardConfig.map((config) => (
              <ComplianceCard
                key={config.id}
                id={config.id}
                title={config.title}
                subtitle={config.subtitle}
                description={config.description}
                logo={config.logo}
                badgeAsset={config.badgeAsset}
                actionLabel={config.actionLabel}
                actionHref={config.actionHref}
                score={
                  isStacksLoading
                    ? null
                    : config.getScore({
                        hasDedicatedStack,
                        hasDedicatedStackInCaCentral,
                      })
                }
                onClick={() => {
                  const score = isStacksLoading
                    ? null
                    : config.getScore({
                        hasDedicatedStack,
                        hasDedicatedStackInCaCentral,
                      });
                  homeDashEvent(HOME_DASH_EVENTS.COMPLIANCE_CARD_OPEN, {
                    cardId: config.id,
                    score,
                    hasDedicatedStack,
                    ...homeDashOrgPayload(org),
                  });
                  setOpenCardId(config.id);
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {openCardId !== null && openConfig && (
        <ModalOverlay
          onClose={() => setOpenCardId(null)}
          aria-label={`${openConfig.title} details`}
          className="px-4 pb-4"
          handleEscape={badgePreview === null}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <Modal
              isOpen
              className="min-w-[400px] max-w-lg"
              onClose={
                badgePreview === null ? () => setOpenCardId(null) : undefined
              }
            >
              <ModalHeading
                icon={
                  openCardScore !== null ? (
                    <Tooltip
                      text={COMPLIANCE_SCORE_TOOLTIP[openCardScore]}
                      placement="top"
                    >
                      <span className="inline-flex items-center">
                        {renderComplianceScoreIcon(openCardScore)}
                      </span>
                    </Tooltip>
                  ) : undefined
                }
                title={openConfig.title}
                description={openConfig.modalSubtitle}
                titleTrailing={
                  openConfig.logo ? (
                    <img
                      src={`${LOGO_BASE_PATH}/${openConfig.logo}`}
                      alt=""
                      className="w-10 h-10 object-contain ml-1"
                    />
                  ) : undefined
                }
                onClose={() => setOpenCardId(null)}
              />
              <ModalBody>
                <div className="text-sm text-gray-600 mb-4">
                  {openConfig.description}
                </div>
                {openConfig.badgeAsset && (
                  <div className="mb-4">
                    <button
                      type="button"
                      onClick={() => {
                        homeDashEvent(
                          HOME_DASH_EVENTS.COMPLIANCE_BADGE_PREVIEW_OPEN,
                          {
                            cardId: openCardId,
                            badgeAsset: openConfig.badgeAsset,
                            ...homeDashOrgPayload(org),
                          },
                        );
                        setBadgePreview(openConfig.badgeAsset ?? null);
                      }}
                      className="rounded border border-gray-200 bg-white p-1 shadow-sm hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400"
                      title="Click to view full size"
                    >
                      <img
                        src={`${BADGE_BASE_PATH}/${openConfig.badgeAsset}`}
                        alt="Compliance badge"
                        className="h-14 w-auto object-contain"
                      />
                    </button>
                  </div>
                )}
                <hr className="border-t border-gray-200 my-4" />
                <div className="flex flex-wrap items-center gap-3">
                  {openConfig.actionHref && openConfig.actionLabel ? (
                    <a
                      href={openConfig.actionHref}
                      className={cn(
                        "inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md border",
                        tokens.buttons.styles.primary,
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        fireModalCta("trust_center");
                        controlsEvent(CONTROLS_EVENTS.TRUST_CENTER_CLICK);
                      }}
                    >
                      {openConfig.actionLabel}
                    </a>
                  ) : canSeeSecurityDashboard ? (
                    <Link
                      to={securityControlsDestination}
                      className={cn(
                        "inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md border",
                        tokens.buttons.styles.primary,
                      )}
                      onClick={() => fireModalCta("view_controls")}
                    >
                      View Controls
                    </Link>
                  ) : (
                    <Tooltip
                      text="Requires Full Visibility on any Environment"
                      placement="top"
                    >
                      <span
                        className={cn(
                          "inline-flex cursor-not-allowed items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border opacity-60",
                          tokens.buttons.styles.primary,
                        )}
                        aria-disabled="true"
                        onClick={() => fireModalCta("view_controls_locked")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            fireModalCta("view_controls_locked");
                          }
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        View Controls
                        <IconLock variant="sm" />
                      </span>
                    </Tooltip>
                  )}
                  {openConfig.docsHref && (
                    <a
                      href={openConfig.docsHref}
                      className={cn(
                        "inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border",
                        tokens.buttons.styles.white,
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => fireModalCta("view_docs")}
                    >
                      View docs
                      <IconExternalLink variant="sm" className="opacity-60" />
                    </a>
                  )}
                </div>
              </ModalBody>
            </Modal>
          </div>
        </ModalOverlay>
      )}

      {badgePreview && (
        <ModalOverlay
          onClose={() => setBadgePreview(null)}
          aria-label="Badge preview"
          className="px-4 pb-4"
        >
          <div
            className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full flex flex-col items-center gap-4"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <div className="flex w-full justify-end">
              <button
                type="button"
                onClick={() => setBadgePreview(null)}
                className="p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                <IconX variant="sm" aria-hidden />
              </button>
            </div>
            <img
              src={`${BADGE_BASE_PATH}/${badgePreview}`}
              alt="Compliance badge full size"
              className="max-h-[70vh] w-auto object-contain"
            />
            <a
              href={`${BADGE_BASE_PATH}/${badgePreview}`}
              download={badgePreview}
              className={cn(
                "inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border",
                tokens.buttons.styles.white,
              )}
              onClick={() =>
                homeDashEvent(HOME_DASH_EVENTS.COMPLIANCE_BADGE_DOWNLOAD, {
                  cardId: openCardId,
                  badgeAsset: badgePreview,
                  ...homeDashOrgPayload(org),
                })
              }
            >
              <IconDownload variant="sm" />
              Download
            </a>
          </div>
        </ModalOverlay>
      )}
    </div>
  );
};
